import React, { useEffect, memo, useState } from 'react';
import { CreditCard, Crown, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCredits } from '../hooks/credits';

// Skeleton loader component - Pre-rendered placeholder
const CreditStatusSkeleton = memo(({ compact = false }: { compact?: boolean }) => (
  <div className={`${compact ? 'p-3' : 'p-5'} bg-gray-100 dark:bg-gray-800/40 rounded-2xl animate-pulse`}>
    <div className="flex items-center space-x-3 mb-4">
      <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div>
        <div className="w-20 h-5 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
        <div className="w-32 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full" />
  </div>
));

// Credit status content with optimized rendering
const CreditStatusContent = memo(({ 
  creditInfo, 
  compact = false 
}: { 
  creditInfo: { credits: number; maxCredits: number; tier: string };
  compact?: boolean;
}) => {
  const [percentage, setPercentage] = useState(0);
  const isLowCredits = creditInfo.credits < 3;
  const tierLabel = creditInfo.tier === 'PRO' ? 'Pro' : 'Free';
  
  // Calculate percentage based on base credits (10) for free tier
  useEffect(() => {
    const baseCredits = creditInfo.tier === 'FREE' ? 10 : creditInfo.maxCredits;
    const newPercentage = Math.min((creditInfo.credits / baseCredits) * 100, 100);
    requestAnimationFrame(() => {
      setPercentage(newPercentage);
    });
  }, [creditInfo.credits, creditInfo.maxCredits, creditInfo.tier]);

  // Custom progress bar styles with additional credits indicator
  const progressBarStyles = () => {
    if (creditInfo.credits < 5) {
      return 'bg-red-500';
    } else if (creditInfo.credits < 10) {
      return 'bg-yellow-500';
    } else {
      return 'bg-blue-500';
    }
  };

  const hasExtraCredits = creditInfo.credits > 10;

  return (
    <div className={`${compact ? 'p-3' : 'p-5'} bg-gray-100 dark:bg-gray-800/40 rounded-2xl`}>
      <div className="flex items-center space-x-3 mb-4">
        {creditInfo.tier === 'PRO' ? (
          <Crown size={24} className="text-amber-500" />
        ) : (
          <CreditCard size={24} className="text-gray-700 dark:text-gray-300" />
        )}
        <div className="flex flex-col">
          <span className="text-lg font-medium text-gray-800 dark:text-gray-200">
            {tierLabel} Plan
          </span>
          <div className="flex items-center space-x-2">
            <span className={`text-sm font-bold ${
              isLowCredits 
                ? 'text-red-500 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {creditInfo.credits} credits remaining
            </span>
            {hasExtraCredits && (
              <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                <Plus size={12} className="mr-1" />
                {creditInfo.credits - 10} bonus
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="relative w-full">
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${progressBarStyles()}`}
            style={{ width: `${Math.max(percentage, 3)}%` }}
          />
        </div>
        {hasExtraCredits && (
          <div className="absolute top-0 right-0 h-3 w-px bg-gray-400 dark:bg-gray-500" 
               style={{ left: '100%' }} />
        )}
      </div>
      
      {creditInfo.tier === 'FREE' && (
        <div className="flex justify-between items-center mt-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Base credits: 10/month
          </p>
          <Link to="/pricing" className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Buy more credits
          </Link>
        </div>
      )}
    </div>
  );
});
// Optimized wrapper with debounced initialization
export const AsyncCreditWrapper = memo(({ children }: { children: React.ReactNode }) => {
  const { initializeCreditStatus } = useCredits();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      initializeCreditStatus();
    }, 100); // Small delay to allow initial render

    return () => clearTimeout(timeoutId);
  }, [initializeCreditStatus]);

  return <>{children}</>;
});

// Main credit status with cached initial state
export const CreditStatus = memo(({ compact = false }: { compact?: boolean }) => {
  const { creditInfo, isLoading } = useCredits();
  
  // Show skeleton immediately without waiting for loading state
  if (!creditInfo) {
    return <CreditStatusSkeleton compact={compact} />;
  }

  return <CreditStatusContent creditInfo={creditInfo} compact={compact} />;
});

// Optimized upgrade button with lazy loading
export const UpgradeButton = memo(() => {
  const { creditInfo } = useCredits();
  
  if (!creditInfo || creditInfo.tier === 'PRO') return null;

  return (
    <Link to="/pricing" className="block">
      <button className="w-full py-3 px-5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-2xl text-lg font-medium transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl">
        <Crown size={20} />
        <span>Upgrade to Pro</span>
      </button>
    </Link>
  );
});

// Optimized warning component with memoized conditions
export const CreditWarning = memo(() => {
  const { creditInfo } = useCredits();
  
  if (!creditInfo?.credits || creditInfo.credits >= 3) return null;

  return (
    <div className="text-center p-3 mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
      <p className="text-red-600 dark:text-red-400 font-semibold text-sm">
        {creditInfo.credits < 2 ? (
          <>
            You need at least 2 credits to process an audio file.
            {creditInfo.tier === 'FREE' && " Consider upgrading to Pro for more credits."}
          </>
        ) : (
          `Low credits! You have ${creditInfo.credits} ${creditInfo.credits === 1 ? 'credit' : 'credits'} remaining.`
        )}
      </p>
    </div>
  );
});